import { useQuery } from '@apollo/client';
import { graphql, useStaticQuery } from 'gatsby';
import React, { FC } from 'react';

import useDeliveryMethod from '@hooks/useDeliveryMethod';

import { keysToCamel } from '@utils/butter';

import { IAddressInfoQueryData } from '@folx-types/address';
import { IAccountInfoQueryData } from '@folx-types/member';

import ACCOUNT_INFO_QUERY from '@graphql/queries/AccountInfo.query.gql';
import ADDRESS_INFO_QUERY from '@graphql/queries/AddressInfo.query.gql';

import ShippingEditStep from './ShippingEditStep.component';

interface IShippingEditStepPageProps {
	path?: string;
}

export interface IShippingEditPageContent {
	butterPage: {
		title: string;
		info: string;
		addressLine1Label: string;
		addressLine2Label: string;
		cityLabel: string;
		stateLabel: string;
		stateFineprint: string;
		zipLabel: string;
		genericError: string;
		capsuleError: string;
	};
}

const ShippingEditStepPage: FC<IShippingEditStepPageProps> = () => {
	const butterData = useStaticQuery<Record<string, string>>(
		graphql`
			{
				butterPage(slug: { eq: "mssp-shipping-edit-page" }) {
					title
					info
					address_line_1_label
					address_line_2_label
					city_label
					state_label
					state_fineprint
					zip_label
					generic_error
					capsule_error
				}
			}
		`
	);

	const {
		loading: accountLoading,
		data: accountData,
		error: accountError,
	} = useQuery<IAccountInfoQueryData>(ACCOUNT_INFO_QUERY);

	const {
		loading: addressLoading,
		data: addressData,
		error: addressError,
	} = useQuery<IAddressInfoQueryData, { memberId: string | undefined }>(ADDRESS_INFO_QUERY, {
		skip: !accountData,
		variables: {
			memberId: accountData?.currentMember?.id,
		},
	});

	const {
		value: deliveryMethod,
		loading: deliveryMethodLoading,
		error: deliveryMethodError,
	} = useDeliveryMethod();

	const address = addressData?.addressByMemberIdAndType;
	const member = accountData?.currentMember;

	const refinedContent: IShippingEditPageContent = keysToCamel(
		butterData
	) as IShippingEditPageContent;

	if (addressLoading || accountLoading) return <div />;

	return (
		<ShippingEditStep
			address={address}
			member={member}
			deliveryMethod={deliveryMethod}
			refinedContent={refinedContent}
		/>
	);
};

export default ShippingEditStepPage;
